import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flower2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function AdminLogin() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/auth/login", { email, password });
      setUser(res.data);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand grid place-items-center px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm"
      >
        <form onSubmit={submit} data-testid="admin-login-form"
          className="bg-white rounded-2xl border border-line p-8 shadow-[0_30px_70px_-40px_rgba(28,36,30,0.35)]">
          <div className="flex items-center gap-2 mb-2">
            <Flower2 className="h-6 w-6 text-sage" />
            <span className="font-serif text-xl text-ink">Elba Rojas</span>
          </div>
          <h1 className="font-serif text-2xl tracking-tight mb-1">Acceso profesional</h1>
          <p className="text-sm text-stonemute mb-8">Panel de contactos de la feria</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-bold text-ink/70">Usuario</Label>
              <Input id="email" type="text" required autoComplete="username" value={email}
                onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email-input"
                className="mt-2 h-11 bg-sand/50 border-line focus-visible:ring-sage" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-bold text-ink/70">Contraseña</Label>
              <Input id="password" type="password" required autoComplete="current-password" value={password}
                onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password-input"
                className="mt-2 h-11 bg-sand/50 border-line focus-visible:ring-sage" />
            </div>
          </div>

          {error && (
            <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5" data-testid="admin-login-error">
              {error}
            </p>
          )}

          <motion.div whileTap={{ scale: 0.98 }} className="mt-6">
            <Button type="submit" disabled={loading} data-testid="admin-login-submit-button"
              className="w-full h-11 rounded-full bg-sage text-white text-sm font-bold transition-colors duration-300 hover:bg-sage-dark">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Entrar al panel"}
            </Button>
          </motion.div>
        </form>
        <a href="/" data-testid="back-to-site-link"
          className="block text-center mt-6 text-xs uppercase tracking-[0.2em] font-semibold text-stonemute transition-colors hover:text-sage">
          Volver a la página
        </a>
      </motion.div>
    </div>
  );
}
